import React from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import RiskTemplate from "../../../assets/others/Mitigator_Controller_Upload_Template-Risk.csv";
import { DataGridView, usePub, Col, HeaderMenu } from "@neumetric/blueprint-lib/components";
import { NMObjectUploadPopup } from "@neumetric/blueprint-lib/flows";
import { useDispatch, useSelector } from "react-redux";
import { mitigatorActions, getRisksList, getImportResult } from "../../../store";

const PER_PAGE = process.env.REACT_APP_INC_PER_PAGE || 9;

const Columns = [
  {
    dataIndex: "risk_id",
    typeRepresentation: "LINK",
    title: "Risk ID",
    width: "10%",
    className: "no-wrap",
  },
  {
    dataIndex: "title",
    typeRepresentation: "TEXT",
    title: "Title",
    searchableFlag: true,
    sortableFlag: true,
    width: "68%",
    searchMode: "SERVER",
    width: "56.5%",
  },
  {
    dataIndex: "score",
    typeRepresentation: "TEXT",
    title: "Score",
    width: "6%",
    sortableFlag: true,
    align: { header: "center", cell: "center" },
  },
  {
    dataIndex: "decision_item",
    typeRepresentation: "DOT",
    title: "Decision",
    width: "8%",
    sortableFlag: true,
  },
  {
    dataIndex: "risk_severity_item",
    typeRepresentation: "DOT",
    title: "Severity",
    width: "7.5%",
    sortableFlag: true,
    filterFlag: true,
  },
];

const RISK_TABS = [
  { label: "Active", id: "active" },
  { label: "Draft", id: "draft" },
  { label: "Archive", id: "archive" },
];

const getRiskStatusFromURL = (search) => {
  const params = new URLSearchParams(search);
  return (params.get("tab") || "draft").toLowerCase();
};

const getPageFromURL = (search) => {
  const params = new URLSearchParams(search);
  return Number(params.get("page") || 1);
};

export const MitigatorRiskTrackerScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const publish = usePub();
  const dispatch = useDispatch();
  const params = useParams();
  const [showImportPopup, setShowImportPopup] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [status, setStatus] = React.useState(getRiskStatusFromURL(location.search));
  const [pageNo, setPageNo] = React.useState(getPageFromURL(location.search));
  const [search, setSearch] = React.useState({}); // column -> value

  const risksList = useSelector(getRisksList.bind(null, status));
  const importResult = useSelector(getImportResult);

  // keep URL in sync (default to tab=active if missing)

  const fetchRisks = React.useCallback(() => {
    dispatch(mitigatorActions.fetchRisks({ pageNo, pageSize: PER_PAGE, search }));
  }, [status, pageNo, search]);

  React.useEffect(() => {
    fetchRisks();
    dispatch(mitigatorActions.setCurrentRisk(null));
  }, [fetchRisks]);

  //  HeaderMenu handlers
  const handleTabChange = React.useCallback(
    (newStatus) => {
      setStatus(newStatus);
      navigate({ pathname: ".", search: `?tab=${newStatus}` }, { replace: true });
    },
    [location.pathname, location.search, navigate, params]
  );

  const handleNewRiskClick = React.useCallback(() => {
    navigate(`/M/R/new`);
  }, [navigate, location.search, location.pathname]);

  const handleMenuClick = React.useCallback(
    (item) => {
      const key = item?.id || item?.key || item;
      if (key === "import-risk") {
        setShowImportPopup(true);
      }
    },
    [publish]
  );

  const handlePageChange = (nextPage) => {
    const params = new URLSearchParams(location.search);
    params.set("page", String(nextPage));
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    setPageNo(nextPage);
  };

  const handleSearch = (dataIndex, value) => {
    const next = { ...search, [dataIndex]: value };
    setSearch(next);
  };

  const handleModalClose = () => {
    setIsSubmitting(false);
    setShowImportPopup(false);
    dispatch(mitigatorActions.clearImportResult());
  };

  const handleUploadConfirm = (files) => {
    if (!files || !files.length) return;
    const rawFile = files[0];
    const file = rawFile.originFileObj || rawFile;
    const action = mitigatorActions.importRisks({ file });
    setIsSubmitting(true);
    dispatch(action);
  };

  const EmptyComponent = <div className="w-100 h-100 d-flex v-ctr h-ctr">There are no risks right now!</div>;
  return (
    <Col className="riskTrackerScreen flex-auto h-100 overflow-hide screen-pad exo2-all">
      <HeaderMenu
        initialTab={status}
        tabItems={RISK_TABS}
        onTabChange={handleTabChange}
        classes={{ root: "pr-20 pt-12 pb-8" }}
        newObject={{
          label: "New Risk",
          icon: "ADD",
          onClick: handleNewRiskClick,
        }}
        menu={{
          items: [
            {
              id: "import-risk",
              label: "Import Risk",
              icon: "UPLOAD",
              variant: "CONTRAST",
            },
          ],
          onMenuClick: handleMenuClick,
        }}
      />

      <div className="col w-100 h-100 overflow-hide mt-12">
        <DataGridView
          columns={Columns}
          dataSource={risksList}
          pagination={{
            pageSize: PER_PAGE,
            totalCount: 0,
            recentFlag: false,
            defaultPage: pageNo,
            onPageChange: handlePageChange,
          }}
          getCellSemantics={(column, record) => {
            const semantics = {};
            if (column.dataIndex === "risk_id") semantics.link = `${record.guid}${location.search}`;
            return semantics;
          }}
          onColumnSearch={handleSearch}
          EmptyComponent={EmptyComponent}
        />
      </div>
      {showImportPopup && (
        <NMObjectUploadPopup
          visible={true}
          onModalClose={handleModalClose}
          onUploadConfirm={handleUploadConfirm}
          isSubmitting={isSubmitting}
          result={importResult}
          steps={[
            "Download the Template provided below. ",
            "Prepare existing Risks using this Template.",
            "Import prepared Risks when ready!",
          ]}
          template={{
            name: `Mitigator_Controller_Upload_Template-Risk.csv`,
            path: RiskTemplate,
          }}
          accept=".csv,.xls,.xlsx"
          multiple={false}
          title="Upload Risks"
          maskClosable={false}
          objectName="Risk"
        />
      )}
    </Col>
  );
};
